import { useEffect, useMemo, type ReactNode } from 'react';
import { resolveLanguage, TranslationContext, type Translate, type Translation } from './context';
import { catalogues, type LanguageCode } from './messages';

interface TranslationProviderProps {
    children: ReactNode;
    /** Override the detected language. Used by tests; nothing in the app passes it. */
    language?: LanguageCode;
}

/**
 * A `?lang=es` override, development only.
 *
 * Checking Spanish layout otherwise means changing the device's language, which is slow enough that it does
 * not get done — and Spanish runs materially longer than English, so it is exactly where a phone layout
 * breaks. Guarded on `import.meta.env.DEV`, so Vite eliminates it from a production build and a guest cannot
 * pin a language the restaurant did not intend.
 */
function developmentOverride(): LanguageCode | null {
    if (!import.meta.env.DEV) return null;

    const requested = new URLSearchParams(window.location.search).get('lang');
    return requested && requested in catalogues ? (requested as LanguageCode) : null;
}

export function TranslationProvider({ children, language }: TranslationProviderProps) {
    const resolved = useMemo(() => language ?? developmentOverride() ?? resolveLanguage(), [language]);

    /*
     * `<html lang>` is not decoration. Screen readers choose a voice and pronunciation rules from it, so
     * Spanish copy under `lang="en"` is read aloud with English phonetics — intelligible to nobody. It also
     * drives hyphenation and quote rendering.
     */
    useEffect(() => {
        document.documentElement.lang = resolved;
    }, [resolved]);

    const value = useMemo<Translation>(() => {
        const catalogue = catalogues[resolved];

        const t: Translate = (key, values) => {
            const template = catalogue[key];

            if (!values) return template;

            // Replace {name} placeholders. Anything unmatched is left as-is rather than blanked: a visible
            // "{table}" is a bug someone reports, an empty gap is one nobody notices.
            return template.replace(/\{(\w+)\}/g, (match, name: string) =>
                name in values ? String(values[name]) : match,
            );
        };

        return { language: resolved, t };
    }, [resolved]);

    return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
}
